import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Accordion from '~/components/Accordion'
import AccordionGroup from '~/components/AccordionGroup'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const AccordionPage: Component = () => {
  const [openAccordion, setOpenAccordion] = createSignal<string | null>(null)

  return (
    <>
      <Title>Accordion Component - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4 italic">Accordion</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Organiza y jerarquiza el contenido de forma compacta. Los acordeones permiten a los usuarios alternar la visibilidad de secciones de contenido, optimizando el espacio vertical.
          </p>
        </div>

        <div class="space-y-12">
          {/* Uso Básico */}
          <Card>
            <Card.Header
              title="Implementación Base"
              subtitle="Estructura simple con título y contenido colapsable."
              icon={<Icon icon="material-symbols-light:list" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-4 p-6 bg-surface-muted rounded-xl border border-border">
                <Accordion title="¿Qué es un Accordion?" icon="material-symbols-light:help-outline">
                  <p class="text-foreground-subtle">
                    Un accordion es un patrón de diseño que consiste en una lista de encabezados apilados verticalmente que revelan u ocultan secciones de contenido asociadas.
                  </p>
                </Accordion>
                <Accordion title="Configuración Avanzada" subtitle="Personaliza el comportamiento y estilo" icon="material-symbols-light:settings-outline">
                  <p class="text-foreground-subtle">Puedes añadir subtítulos, iconos personalizados y controlar el estado de apertura de forma programática.</p>
                </Accordion>
              </div>
            </Card.Body>
          </Card>

          {/* Variantes Visuales */}
          <Card>
            <Card.Header
              title="Estilos y Variantes"
              subtitle="Tres variantes de diseño para integrarse en cualquier tipo de interfaz."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="space-y-3">
                  <Badge color="secondary" variant="soft" size="sm">
                    default
                  </Badge>
                  <Accordion title="Fondo Sutil" variant="default" color="primary">
                    Estilo estándar con relleno suave.
                  </Accordion>
                </div>
                <div class="space-y-3">
                  <Badge color="secondary" variant="soft" size="sm">
                    elevation
                  </Badge>
                  <Accordion title="Sombra y Relieve" variant="elevation" color="success">
                    Efecto de profundidad con sombras suaves.
                  </Accordion>
                </div>
                <div class="space-y-3">
                  <Badge color="secondary" variant="soft" size="sm">
                    outlined
                  </Badge>
                  <Accordion title="Borde Definido" variant="outlined" color="info">
                    Fondo transparente y bordes marcados.
                  </Accordion>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Accordion Group */}
          <Card>
            <Card.Header
              title="Agrupaciones e Interacción"
              subtitle="Coordina múltiples acordeones para que funcionen como una unidad coherente."
              icon={<Icon icon="material-symbols-light:layers-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-8">
                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <h4 class="font-bold mb-4 text-foreground">Apertura Única (Default)</h4>
                  <AccordionGroup>
                    <Accordion title="Servicios en la Nube" icon="material-symbols-light:cloud-outline">
                      Detalles sobre almacenamiento y computación.
                    </Accordion>
                    <Accordion title="Seguridad Perimetral" icon="material-symbols-light:verified-outline">
                      Protocolos de firewall y protección DDoS.
                    </Accordion>
                    <Accordion title="Soporte 24/7" icon="material-symbols-light:headphones-outline">
                      Asistencia técnica garantizada.
                    </Accordion>
                  </AccordionGroup>
                </div>

                <div class="p-8 bg-surface-muted rounded-xl border border-border">
                  <div class="flex justify-between items-center mb-4">
                    <h4 class="font-bold text-foreground">Modo Múltiple y Controlado</h4>
                    <Button size="sm" color="danger" variant="text" onClick={() => setOpenAccordion(null)} startIcon="material-symbols-light:close">
                      Cerrar todos
                    </Button>
                  </div>
                  <AccordionGroup multiple value={openAccordion() ? [openAccordion()!] : []} onValueChange={v => (typeof v === 'string' ? setOpenAccordion(v) : setOpenAccordion(v?.[0] || null))}>
                    <Accordion title="Base de Datos" value="db" color="primary" variant="outlined">
                      Optimización de queries y backups.
                    </Accordion>
                    <Accordion title="Infraestructura" value="infra" color="primary" variant="outlined">
                      Gestión de servidores y clusters.
                    </Accordion>
                  </AccordionGroup>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Caso de Uso: FAQ */}
          <Card>
            <Card.Header
              title="Ejemplo Práctico: FAQ"
              subtitle="Una sección de preguntas frecuentes completa utilizando variantes de elevación."
              icon={<Icon icon="material-symbols-light:phone-in-talk" class="text-indigo-500" size={24} />}
            />
            <Card.Body>
              <div class="max-w-3xl mx-auto space-y-4 p-2">
                <Accordion title="¿Cómo funciona el sistema de créditos?" variant="elevation" icon="material-symbols-light:attach-money">
                  <div class="space-y-3 p-1">
                    <p>Nuestro sistema se basa en tokens mensuales que puedes usar en cualquier servicio.</p>
                    <ul class="list-disc pl-5 text-sm opacity-80 space-y-1">
                      <li>Los créditos no expiran a fin de mes.</li>
                      <li>Puedes comprar paquetes adicionales en cualquier momento.</li>
                    </ul>
                  </div>
                </Accordion>
                <Accordion title="¿Es compatible con mi tecnología actual?" variant="elevation" icon="material-symbols-light:memory-outline">
                  Soportamos integraciones nativas con las principales plataformas del mercado a través de nuestra API REST robusta.
                </Accordion>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Estructura de datos para los componentes Accordion y AccordionGroup."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <div>
                  <h4 class="font-bold mb-4 text-foreground border-l-4 border-primary-500 pl-3">AccordionPropsType</h4>
                  <div class="overflow-x-auto rounded-xl border border-border">
                    <table class="w-full text-sm text-left">
                      <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                        <tr>
                          <th class="px-6 py-4 border-b">Propiedad</th>
                          <th class="px-6 py-4 border-b">Tipo</th>
                          <th class="px-6 py-4 border-b">Default</th>
                          <th class="px-6 py-4 border-b">Descripción</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-border">
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">title</td>
                          <td class="px-6 py-4">string</td>
                          <td class="px-6 py-4">-</td>
                          <td class="px-6 py-4">Texto principal del encabezado.</td>
                        </tr>
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">subtitle</td>
                          <td class="px-6 py-4">string</td>
                          <td class="px-6 py-4">-</td>
                          <td class="px-6 py-4">Texto secundario opcional.</td>
                        </tr>
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">icon</td>
                          <td class="px-6 py-4">string</td>
                          <td class="px-6 py-4">-</td>
                          <td class="px-6 py-4">Icono decorativo lateral.</td>
                        </tr>
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">variant</td>
                          <td class="px-6 py-4">'default' | 'elevation' | 'outlined'</td>
                          <td class="px-6 py-4">'default'</td>
                          <td class="px-6 py-4">Estilo visual del contenedor.</td>
                        </tr>
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">color</td>
                          <td class="px-6 py-4">AccordionColorType</td>
                          <td class="px-6 py-4">'default'</td>
                          <td class="px-6 py-4">Contexto de color para el componente.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div>
                  <h4 class="font-bold mb-4 text-foreground border-l-4 border-secondary-500 pl-3">AccordionGroupPropsType</h4>
                  <div class="overflow-x-auto rounded-xl border border-border">
                    <table class="w-full text-sm text-left">
                      <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                        <tr>
                          <th class="px-6 py-4 border-b">Propiedad</th>
                          <th class="px-6 py-4 border-b">Tipo</th>
                          <th class="px-6 py-4 border-b">Default</th>
                          <th class="px-6 py-4 border-b">Descripción</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-border">
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">multiple</td>
                          <td class="px-6 py-4">boolean</td>
                          <td class="px-6 py-4">false</td>
                          <td class="px-6 py-4">Permite abrir varios elementos a la vez.</td>
                        </tr>
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">value</td>
                          <td class="px-6 py-4">string | string[]</td>
                          <td class="px-6 py-4">-</td>
                          <td class="px-6 py-4">Controla el estado desde el padre.</td>
                        </tr>
                        <tr>
                          <td class="px-6 py-4 font-mono font-bold">divider</td>
                          <td class="px-6 py-4">boolean</td>
                          <td class="px-6 py-4">true</td>
                          <td class="px-6 py-4">Muestra líneas divisorias entre elementos.</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default AccordionPage
